import { translate } from "@/i18n";
import { libLabelsMessages } from "@/i18n/messages/lib-labels";

/** Metadata-only entry — lives in the static registry. */
export interface SlashCommandMeta {
  name: string;
  description: string;
  icon: string;
  type: "command" | "skill";
  template?: string;
  /** When true, the command accepts a free-form argument typed after a space (e.g. `/max 5`). */
  accepts_arg?: boolean;
}

/** Runtime entry with live handler — built by parent from metadata. */
export interface SlashCommand extends SlashCommandMeta {
  /** Handler receives the trimmed argument string when `accepts_arg` is true. */
  handler?: (arg?: string) => void;
  /** Live status text shown in the popup row (e.g. "Currently 4", "ON"). */
  status?: string;
}

/** Built-in commands (handlers injected at runtime by MasterLayout); each
 *  description reads in the app language when it is read. */
export const BUILT_IN_COMMANDS: SlashCommandMeta[] = [
  {
    name: "new",
    get description() {
      return translate(libLabelsMessages, "commandNew");
    },
    icon: "Plus",
    type: "command",
  },
  {
    name: "tidy",
    get description() {
      return translate(libLabelsMessages, "commandTidy");
    },
    icon: "Maximize",
    type: "command",
  },
  {
    name: "focus",
    get description() {
      return translate(libLabelsMessages, "commandFocus");
    },
    icon: "Target",
    type: "command",
  },
  {
    name: "board",
    get description() {
      return translate(libLabelsMessages, "commandBoard");
    },
    icon: "Kanban",
    type: "command",
  },
  {
    name: "review",
    get description() {
      return translate(libLabelsMessages, "commandReview");
    },
    icon: "Inbox",
    type: "command",
  },
  {
    name: "heart",
    get description() {
      return translate(libLabelsMessages, "commandHeart");
    },
    icon: "Heart",
    type: "command",
  },
  {
    name: "voice",
    get description() {
      return translate(libLabelsMessages, "commandVoice");
    },
    icon: "Volume2",
    type: "command",
    accepts_arg: true,
  },
  {
    name: "max",
    get description() {
      return translate(libLabelsMessages, "commandMax");
    },
    icon: "Gauge",
    type: "command",
    accepts_arg: true,
  },
];

/** All built-in metadata entries (commands only — skills come from the server). */
export const ALL_BUILT_IN: SlashCommandMeta[] = [...BUILT_IN_COMMANDS];

/**
 * Returns the flat filtered list in the same order as rendered
 * (commands first, skills second). Use this for keyboard navigation indexing.
 */
export function getFilteredSlashCommands(
  commands: SlashCommand[],
  query: string,
): SlashCommand[] {
  const filtered = commands.filter((c) =>
    c.name.toLowerCase().startsWith(query.toLowerCase()),
  );
  const cmds = filtered.filter((c) => c.type === "command");
  const skills = filtered.filter((c) => c.type === "skill");
  return [...cmds, ...skills];
}
